import {
	useController,
	useFormContext,
	useFormState,
	useWatch
} from "react-hook-form";
import { AdminCCEmailRecipientBlock } from "src/features/Admin/Observations/components/AdminCCEmailRecipientBlock";
import { AdminDateBlock } from "src/features/Admin/Observations/components/AdminDateBlock";
import { AdminEmployeeSelectionBlock } from "src/features/Admin/Observations/components/AdminEmployeeSelectionBlock";
import { AdminObservationDateBlock } from "src/features/Admin/Observations/components/AdminObservationDateBlock";
import { AdminTextBlock } from "src/features/Admin/Observations/components/AdminTextBlock";
import { AdminImageUploadBlock } from "src/features/Admin/Observations/components/AdminImageUploadBlock";
import { AdminRadioBlock } from "src/features/Admin/Observations/components/AdminRadioBlock";
import { AdminSelectBlock } from "src/features/Admin/Observations/components/AdminSelectBlock";
import { QuestionActions } from "src/features/observationsAdmin/components/QuestionActions";

interface Props extends AdminObservationQuestion {
	questionIndex: number;
	questionType: any;
	isQuestionActionsVisible?: boolean;
	removeQuestion: () => void;
	moveQuestionDown: (
		index: number,
		answerOne: number | null,
		answerTwo: number | null
	) => void;
	moveQuestionUp: (
		index: number,
		answerOne: number | null,
		answerTwo: number | null
	) => void;
	onBranchedQuestionsPreviewOpen?: () => void;
}

const AdminObservationQuestion = ({
	questionIndex,
	questionType,
	isQuestionActionsVisible,
	removeQuestion,
	moveQuestionDown,
	moveQuestionUp,
	onBranchedQuestionsPreviewOpen
}: Props) => {
	const { control, trigger } = useFormContext<AdminObservationForm>();
	const formState = useFormState({ control });

	const { field: questionField } = useController({
		control,
		name: `questions.${questionIndex}.question`,
		rules: { required: "Please complete required field" }
	});

	const { field: noteField } = useController({
		control,
		name: `questions.${questionIndex}.note`
	});

	const { field: isActionRequiredField } = useController({
		control,
		name: `questions.${questionIndex}.isActionRequired`
	});

	const { field: correctAnswerRequiredField } = useController({
		control,
		name: `questions.${questionIndex}.isCorrectAnswerRequired`
	});

	const observationType = useWatch({
		control,
		name: "observationType"
	});

	const questions = useWatch({
		control,
		name: "questions"
	});

	const question = useWatch({
		control,
		name: `questions.${questionIndex}`
	});

	const errors = {
		question: formState.errors?.questions?.[questionIndex]?.question?.message
	};

	const handleQuestionBlur = () => {
		// This blur is specifically for the select or answer block
		// We need to validate the answers list for that question on blur
		trigger(`questions.${questionIndex}`);
	};

	return (
		<div className="flex relative flex-col rounded-xl border border-border bg-subtle shadow-sm p-6">
			{isQuestionActionsVisible && (
				<QuestionActions
					questionIndex={questionIndex}
					questions={questions}
					moveQuestionUp={moveQuestionUp}
					moveQuestionDown={moveQuestionDown}
					removeQuestion={removeQuestion}
					isBranchButtonVisible={true}
					onBranchedQuestionsPreviewOpen={onBranchedQuestionsPreviewOpen}
				/>
			)}

			{questionType === "TEXT" && (
				<AdminTextBlock
					questionIndex={questionIndex}
					question={questionField}
					note={noteField}
					isActionRequired={isActionRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
				/>
			)}

			{questionType === "DATE" && (
				<AdminDateBlock
					questionIndex={questionIndex}
					question={questionField}
					isActionRequired={isActionRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
				/>
			)}

			{questionType === "OBSERVATION_DATE" && (
				<AdminObservationDateBlock
					questionIndex={questionIndex}
					question={questionField}
					isActionRequired={isActionRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
				/>
			)}

			{questionType === "CC_EMAIL_RECIPIENT" && (
				<AdminCCEmailRecipientBlock
					questionIndex={questionIndex}
					question={questionField}
					note={noteField}
					isActionRequired={isActionRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
				/>
			)}

			{questionType === "EMPLOYEE_SELECTION" && (
				<AdminEmployeeSelectionBlock
					questionIndex={questionIndex}
					question={questionField}
					note={noteField}
					isActionRequired={isActionRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
				/>
			)}

			{questionType === "IMAGE_UPLOAD" && (
				<AdminImageUploadBlock
					questionIndex={questionIndex}
					question={questionField}
					note={noteField}
					isActionRequired={isActionRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
				/>
			)}

			{questionType === "RADIO" && (
				<AdminRadioBlock
					question={questionField}
					questionIndex={questionIndex}
					note={noteField}
					isActionRequired={isActionRequiredField}
					observationType={observationType}
					isCorrectAnswerRequired={correctAnswerRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
					onBlur={handleQuestionBlur}
				/>
			)}

			{questionType === "SELECT" && (
				<AdminSelectBlock
					question={questionField}
					questionIndex={questionIndex}
					note={noteField}
					isActionRequired={isActionRequiredField}
					observationType={observationType}
					isCorrectAnswerRequired={correctAnswerRequiredField}
					isSettingsDisabled={!question.id}
					errors={errors}
					onBlur={handleQuestionBlur}
				/>
			)}
		</div>
	);
};

export default AdminObservationQuestion;
